import { join } from 'path-browserify'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { API } from '@/api'
import ArrowRightIcon from '@/assets/icons/arrow-right.svg?react'
import FolderIcon from '@/assets/icons/folder.svg?react'
import FSDeleteIcon from '@/assets/icons/fs-delete.svg?react'
import FSDownloadIcon from '@/assets/icons/fs-download.svg?react'
import FSMoveIcon from '@/assets/icons/fs-move.svg?react'
import FSRenameIcon from '@/assets/icons/fs-rename.svg?react'
import { Button, Checkbox, Divider, Loading, message, Modal, Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown'
import { useUserStore } from '@/stores/user'
import { formatBytes, formatDatePreset, formatGB2Bytes, formatMB2Bytes } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { InsufficientQuotaView } from '../../components/InsufficientQuotaView'
import CreateFolder from './create-folder'
import { IFile } from './fs'
import * as fs from './fs'
import { useDelete } from './useDelete'
import { useMove } from './useMove'
import { useRename } from './useRename'
import { FILE_STATUS, useUpload } from './useUpload'
export function DataMgrPage() {
  const { t } = useTranslation()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [renameInEdit, setRenameInEdit] = useState<IFile | undefined>()
  const [moveInEdit, setMoveInEdit] = useState<IFile[] | undefined>()
  const [selectedRows, setSelectedRows] = useState<IFile[]>([])
  const [files, setFiles] = useState<IFile[]>([])
  const [uploadList, uploadFile, successNum] = useUpload()
  const { deleteFiles } = useDelete()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [currentDomain, setCurrentDomain] = useState(0)
  const [currentDomainVolume, setCurrentDomainVolume] = useState<{
    quota_gb: number
    usage_mb: number
  } | null>(null)
  // Store volumes for dropdown usage
  const [volumes, setVolumes] = useState<any[]>([])
  const prevDomainRef = useRef(currentDomain)
  const hasService = useUserStore((state) => Boolean(state.domainCurrentServiceMap[currentDomain]))
  const userStore = useUserStore()
  const notAvailable = !userStore.partVolume?.quota_gb
  const [loading, setLoading] = useState(false)
  const [domainInfos, setDomainInfos] = useState<any[]>([])
  const [showMgrInstruction, setShowMgrInstruction] = useState(false)

  const dirPath = useMemo(() => join(searchParams.get('path') || '/', '/'), [searchParams])
  const pathList = useMemo(() => dirPath.split('/').slice(1, -1), [dirPath])
  const getPathUrl = (path: string) => `/data?path=${encodeURIComponent(join('/', path))}`

  const { RenameDialog } = useRename(currentDomain)
  const { MoveDialog } = useMove(currentDomain)

  const getDomains = useCallback(async () => {
    setLoading(true)
    try {
      const [domainRes, volumesRes] = await Promise.all([API.getDomainList(), API.getVolumes()])

      if (domainRes.code === 0 && volumesRes.code === 0) {
        // Filter out domain 3(Japan) and domains with no volumes or zero quota
        const filteredDomainInfos = domainRes.domainInfos.filter((info: any) => {
          if (info.domain === 3) return false

          const domainVolumes = volumesRes.volumes.filter(
            (volume: any) => volume.domain === info.domain,
          )

          // Check if domain has any volumes and if any volume has non-zero quota
          return (
            domainVolumes.length > 0 && domainVolumes.some((volume: any) => volume.quota_gb > 0)
          )
        })

        setDomainInfos(filteredDomainInfos)
        setVolumes(volumesRes.volumes)

        // Set currentDomain to the first available domain if there are any
        if (filteredDomainInfos.length > 0) {
          setCurrentDomain(filteredDomainInfos[0].domain)
          setCurrentDomainVolume(
            volumesRes.volumes.find(
              (volume: any) => volume.domain === filteredDomainInfos[0].domain,
            ),
          )
          setShowMgrInstruction(false)
        } else {
          setShowMgrInstruction(true)
        }
      }
    } finally {
      setLoading(false)
    }
  }, [])

  const uploadingTasks = useMemo(() => {
    return uploadList.filter(
      (task) => ![FILE_STATUS.SUCCESS, FILE_STATUS.FAILED].includes(task.status),
    )
  }, [uploadList])

  const fetchFiles = async (p = dirPath, domain = 0) => {
    if (!hasService) return

    setLoading(true)
    try {
      const { code, msg, files } = await fs.ls({ path: p, domain })

      if (code === 0) {
        setSelectedRows([])
        setFiles(files)
      } else if (code === 15) {
        navigate('/data')
      } else if (code === -4) {
        if (msg.startsWith('timeout of')) {
          message.error(t('errorMsg.fs.timeout'))
        } else if (msg === 'Network Error') {
          message.error(t('errorMsg.fs.networkError'))
        }
      } else {
        message.error(t('errorMsg.fs.getListFailed'))
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleRowSelection = (file: IFile, checked: boolean) => {
    if (checked) {
      setSelectedRows((prev) => [...prev, file])
    } else {
      setSelectedRows((prev) => prev.filter((f) => f.id !== file.id))
    }
  }

  const onFileChange = ({ target }: React.SyntheticEvent) => {
    if (!currentDomainVolume) return
    const targetInput = target as HTMLInputElement

    if (targetInput.files?.length) {
      const fileSize = targetInput.files[0].size
      const quotaBytes = formatGB2Bytes(currentDomainVolume.quota_gb)
      const usedBytes = formatMB2Bytes(currentDomainVolume.usage_mb)
      const remainingBytes = quotaBytes - usedBytes

      // check if the file is too large
      if (fileSize > remainingBytes) {
        message.error(t('common.errorCodes.fs.17'))
        return
      }

      uploadFile(targetInput.files[0], dirPath, currentDomain)
    }

    targetInput.value = ''
  }

  const onRowDeleteClick = (row: IFile) => {
    setSelectedRows([row])
    setDeleteModalOpen(true)
  }
  const onFileDeleteClick = async () => {
    setLoading(true)
    try {
      const failedList = await deleteFiles(selectedRows, dirPath, currentDomain)

      if (failedList.length) {
        message.error('Delete failed')
      } else {
        message.success(t('common.message.success'))
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }

    setDeleteModalOpen(false)
    fetchFiles(dirPath, currentDomain)
  }

  const onRowRenameClick = (row: IFile) => {
    setRenameInEdit(row)
  }

  const onRowMoveClick = (row: IFile) => {
    setMoveInEdit([row])
  }

  useEffect(() => {
    if (prevDomainRef.current !== currentDomain) {
      navigate(getPathUrl('/'))
      fetchFiles('/', currentDomain)
      prevDomainRef.current = currentDomain
    } else {
      fetchFiles(dirPath, currentDomain)
    }
  }, [currentDomain, dirPath, successNum])

  useEffect(() => {
    getDomains()
  }, [])

  return (
    <div className="card bg-white h-full overflow-y-auto">
      <div className="flex h-[60px] items-start justify-start gap-4">
        <div className="h6">
          <Trans i18nKey="data.title">Data Hub</Trans>
        </div>
        {userStore.isOrderExpired ? (
          <div
            className={cn(
              'flex px-5 py-1 justify-center items-center gap-2 rounded-full cursor-pointer bg-[#FFEBEC]',
            )}
            onClick={() => navigate('/space')}
          >
            <div className="text-error">⚠️️</div>
            <div>
              <span className="t-sb text-error">
                Your storage capacity has expired, please upgrade your plan
              </span>
            </div>
            <div>
              <ArrowRightIcon className="w-3 h-3 text-error" />
            </div>
          </div>
        ) : null}
      </div>

      <Divider />

      {loading ? (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      ) : (
        <>
          {domainInfos.length > 0 && (
            <div className="flex items-center justify-between">
              <Dropdown
                rootClassName="t-sb"
                className="h-11"
                options={domainInfos.map((info) => ({
                  label: t(`common.region.domain${info.domain}`),
                  value: info.domain,
                  className: 't-sm',
                }))}
                value={currentDomain}
                onChange={(value) => {
                  setCurrentDomain(Number(value))
                  // Update currentDomainVolume based on selected domain and stored volumes
                  const selectedVolume = volumes.find(
                    (volume: any) => volume.domain === Number(value),
                  )
                  setCurrentDomainVolume(selectedVolume || null)
                }}
              />
              {hasService && (
                <div className="flex gap-2">
                  <Button
                    type="warning"
                    disabled={!selectedRows.length}
                    onClick={() => setDeleteModalOpen(true)}
                  >
                    <Trans i18nKey="data.deleteBtn">Delete</Trans>
                  </Button>
                  {!userStore.isOrderExpired && (
                    <Button
                      disabled={!selectedRows.length}
                      onClick={() => setMoveInEdit(selectedRows)}
                    >
                      <Trans i18nKey="data.moveBtn">Move</Trans>
                    </Button>
                  )}
                  <Button
                    onClick={() =>
                      fetchFiles(dirPath, currentDomain).then(() => {
                        message.success('Update completed')
                      })
                    }
                  >
                    <Trans i18nKey="data.refreshBtn">Refresh</Trans>
                  </Button>
                  <CreateFolder
                    dir={dirPath}
                    files={files}
                    onOk={() => fetchFiles(dirPath, currentDomain)}
                    domain={currentDomain}
                    disabled={userStore.isOrderExpired}
                  />
                  <label
                    className={cn(
                      'rounded-[8px] t-sb px-4 py-3',
                      userStore.isOrderExpired
                        ? 'bg-btn-secondary/50 text-primary-reverse/50 cursor-not-allowed'
                        : 'bg-btn-secondary text-primary-reverse hover:bg-btn-secondary-hover cursor-pointer',
                    )}
                  >
                    <Trans i18nKey="data.uploadBtn">Upload</Trans>
                    <input
                      type="file"
                      className="hidden"
                      onChange={userStore.isOrderExpired ? undefined : onFileChange}
                      disabled={userStore.isOrderExpired}
                    />
                  </label>
                </div>
              )}
            </div>
          )}

          {domainInfos.length === 0 && showMgrInstruction && (
            <div className="mt-4 mb-2">
              <InsufficientQuotaView />
            </div>
          )}

          {hasService && (!notAvailable || userStore.isOrderExpired) && domainInfos.length > 0 && (
            <div>
              <div className="mt-4 mb-2 flex justify-between t-sb text-secondary">
                <div className="flex items-center gap-1">
                  <Link to={getPathUrl('/')}>
                    <Trans i18nKey="data.allFiles">All files</Trans>
                  </Link>
                  {pathList.map((path, index) => (
                    <div key={index} className="flex items-center gap-1">
                      <div className="text-tertiary">/</div>
                      <Link to={getPathUrl(pathList.slice(0, index + 1).join('/'))}>{path}</Link>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  {uploadingTasks.length > 0 && (
                    <div className="text-btn-secondary">
                      <Trans i18nKey="data.selectedFiles">
                        {{ select: selectedRows.length }} selected
                      </Trans>
                    </div>
                  )}
                  <div>
                    <Trans i18nKey="data.totalFiles">Total {{ total: files.length }} files</Trans>
                  </div>
                </div>
              </div>

              <table className="table">
                <thead>
                  <tr>
                    <th></th>
                    <th>
                      <Trans i18nKey="data.tableHeaderName">Name</Trans>
                    </th>
                    <th>
                      <Trans i18nKey="data.tableHeaderSize">Size</Trans>
                    </th>
                    <th>
                      <Trans i18nKey="data.tableHeaderTime">Time</Trans>
                    </th>
                    <th>
                      <Trans i18nKey="data.tableHeaderAction">Action</Trans>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {uploadingTasks.map((item, idx) => (
                    <tr key={idx} className="group hover:bg-panel-gray-100">
                      <td className="w-[52px]">
                        <Checkbox disabled />
                      </td>
                      <td>{item.name}</td>
                      <td className="w-[120px]">{formatBytes(item.size)}</td>
                      <td className="text-tertiary w-[200px]">-</td>
                      <td className="w-[200px] text-tertiary">
                        Uploading...
                        {item.loaded && item.size > 0
                          ? ` ${((100 * item.loaded) / item.size).toFixed(1)}%`
                          : ''}
                      </td>
                    </tr>
                  ))}
                  {files.map((file, idx) => (
                    <tr key={idx} className="group hover:bg-panel-gray-100">
                      <td className="w-[52px]" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedRows.some((row) => row.id === file.id)}
                          onChange={(checked) => handleRowSelection(file, checked)}
                        />
                      </td>
                      <td
                        className="cursor-pointer"
                        onClick={() => {
                          if (file.isDir) {
                            navigate(getPathUrl(join(dirPath, file.name)))
                          } else {
                            const isSelected = selectedRows.some((row) => row.id === file.id)
                            handleRowSelection(file, !isSelected)
                          }
                        }}
                      >
                        {file.isDir ? (
                          <span className="flex items-center gap-x-2">
                            <FolderIcon className="text-warning" />
                            <span>{file.name}</span>
                          </span>
                        ) : (
                          file.name
                        )}
                      </td>
                      <td className="w-[120px]">{formatBytes(file.size)}</td>
                      <td className="text-tertiary w-[200px]">
                        {formatDatePreset(file.updateTime * 1000, 'second')}
                      </td>
                      <td className="w-[200px]">
                        <div className="gap-4 hidden group-hover:flex">
                          <FSDownloadIcon
                            className={cn(
                              'w-5 h-5 cursor-pointer text-tertiary',
                              file.isDir ? ' cursor-not-allowed' : 'hover:text-primary',
                            )}
                            onClick={() => fs.download({ path: file.path, domain: currentDomain })}
                          />
                          <FSMoveIcon
                            className={cn(
                              'w-5 h-5',
                              userStore.isOrderExpired
                                ? 'cursor-not-allowed text-tertiary/50'
                                : 'cursor-pointer text-tertiary hover:text-primary',
                            )}
                            onClick={() => !userStore.isOrderExpired && onRowMoveClick(file)}
                          />
                          <FSRenameIcon
                            className={cn(
                              'w-5 h-5',
                              userStore.isOrderExpired
                                ? 'cursor-not-allowed text-tertiary/50'
                                : 'cursor-pointer text-tertiary hover:text-primary',
                            )}
                            onClick={() => !userStore.isOrderExpired && onRowRenameClick(file)}
                          />
                          <FSDeleteIcon
                            className="w-5 h-5 cursor-pointer text-error"
                            onClick={() => onRowDeleteClick(file)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <Modal.Header>Confirmation</Modal.Header>
        <Modal.Body>Are you sure you want to delete the file(s)?</Modal.Body>
        <Modal.Footer>
          <Button type="outline" onClick={() => setDeleteModalOpen(false)}>
            <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
          </Button>
          <Button type="danger" onClick={onFileDeleteClick}>
            <Trans i18nKey="common.confirmModalOk">Confirm</Trans>
          </Button>
        </Modal.Footer>
      </Modal>
      <RenameDialog
        file={renameInEdit}
        files={
          renameInEdit && Array.isArray(files) ? files.filter((f) => f.id != renameInEdit.id) : []
        }
        onCancel={() => setRenameInEdit(undefined)}
        onOk={() => {
          setRenameInEdit(undefined)
          fetchFiles(dirPath, currentDomain)
        }}
      />
      {Array.isArray(moveInEdit) && moveInEdit.length > 0 ? (
        <MoveDialog
          onCancel={() => setMoveInEdit(undefined)}
          onOk={() => {
            setMoveInEdit(undefined)
            fetchFiles(dirPath, currentDomain)
          }}
          files={moveInEdit}
        />
      ) : null}
    </div>
  )
}
